/**
 * Shop filter bar behaviour.
 *
 *   Theme + Material — tag dropdowns. Combined with AND using Shopify's tag URL
 *            (/collections/all/patriotic+material_slate). Server-rendered.
 *   Sort   — navigates to the same URL with sort_by set.
 *   Show   — caps how many cards are visible (10, 20, All). Display
 *            preference only, remembered per visitor.
 */
const STORE_KEY = 'nh-shop-show';

class NutthouseShopFilters extends HTMLElement {
  connectedCallback() {
    this.sort = this.querySelector('[data-nh-sort]');
    this.show = this.querySelector('[data-nh-show]');
    this.tagSelects = Array.from(this.querySelectorAll('[data-nh-tag]'));
    this.countEl = this.querySelector('[data-nh-count]');
    this.base = this.querySelector('[data-nh-base]')?.dataset.nhBase || window.location.pathname;

    this.sort?.addEventListener('change', this.#onSort);
    this.show?.addEventListener('change', this.#onShow);
    this.tagSelects.forEach((sel) => sel.addEventListener('change', this.#onTag));

    const initial = this.#stored() ?? this.show?.options[0]?.value ?? 'all';
    if (this.show) this.show.value = initial;
    this.#applyShow(initial, false);

    const grid = this.#grid();
    if (grid) {
      this.observer = new MutationObserver(() => this.#applyShow(this.current, false));
      this.observer.observe(grid, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #grid() {
    return document.querySelector('.product-grid, [data-testid="product-grid"], .collection__grid');
  }

  #cards() {
    return Array.from(document.querySelectorAll('.product-grid__item'));
  }

  #stored() {
    try {
      const v = localStorage.getItem(STORE_KEY);
      if (!v) return null;
      return Array.from(this.show?.options || []).some((o) => o.value === v) ? v : null;
    } catch {
      return null;
    }
  }

  #onTag = () => {
    const tags = this.tagSelects.map((sel) => sel.value).filter(Boolean).map(encodeURIComponent);
    const url = new URL(window.location.href);
    url.pathname = tags.length ? this.base.replace(/\/$/, '') + '/' + tags.join('+') : this.base;
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  #onSort = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.sort.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  #onShow = () => {
    this.#applyShow(this.show.value, true);
  };

  #applyShow(value, persist) {
    this.current = value;
    const limit = value === 'all' ? 0 : Number(value) || 0;

    const cards = this.#cards();
    cards.forEach((card, i) => {
      card.hidden = limit > 0 && i >= limit;
    });

    if (this.countEl) {
      const shown = limit > 0 ? Math.min(limit, cards.length) : cards.length;
      this.countEl.textContent = this.countEl.dataset.nhTemplate
        .replace('[shown]', shown)
        .replace('[total]', this.countEl.dataset.nhTotal);
    }

    if (!persist) return;
    try {
      localStorage.setItem(STORE_KEY, String(value));
    } catch {}
  }
}

if (!customElements.get('nutthouse-shop-filters')) {
  customElements.define('nutthouse-shop-filters', NutthouseShopFilters);
}
